(() => {
  if (customElements.get('ak-inline-search')) return;

  class InlineSearch extends HTMLElement {
    connectedCallback() {
      if (this.layer) return;
      this.header = this.closest('.shopify-section');
      this.desktopInput = this.querySelector('input[type="search"]');
      this.layer = this.querySelector('template').content.firstElementChild.cloneNode(true);
      document.body.append(this.layer);
      this.panel = this.layer.querySelector('.ak-search-panel');
      this.mobileInput = this.layer.querySelector('input[type="search"]');
      this.inputs = [this.desktopInput, this.mobileInput];
      this.results = this.layer.querySelector('.ak-search-results');
      this.initial = this.layer.querySelector('.ak-search-initial');
      this.loading = this.layer.querySelector('.ak-search-loading');
      this.error = this.layer.querySelector('.ak-search-error');
      this.status = this.layer.querySelector('.ak-search-status');
      this.sequence = 0;
      this.activeIndex = -1;
      this.options = [];
      this.lifecycle = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.lifecycle.signal });
      this.inputs.forEach(input => {
        on(input, 'focus', () => this.open(input));
        on(input, 'input', event => { if (!event.isComposing) this.update(input.value); });
        on(input, 'compositionend', () => this.update(input.value));
        on(input, 'keydown', event => this.keydown(event));
        on(input.form, 'submit', event => {
          if (!input.value.trim()) { event.preventDefault(); input.focus(); }
        });
        on(input.form, 'reset', event => { event.preventDefault(); this.update(''); input.focus(); });
      });
      this.openers = Array.from(this.header.querySelectorAll('[data-ak-search-open]'));
      this.openers.forEach(button => on(button, 'click', () => {
        this.opener = button;
        this.open(this.mobileInput);
        this.mobileInput.focus();
      }));
      on(this.layer.querySelector('.ak-search-close'), 'click', () => this.close(true));
      on(this.layer.querySelector('.ak-search-backdrop'), 'click', () => this.close(false));
      on(document, 'pointerdown', event => {
        if (!this.contains(event.target) && !this.layer.contains(event.target) && !this.openers.some(button => button.contains(event.target))) this.close(false);
      });
      on(document, 'focusin', event => {
        if (!this.contains(event.target) && !this.layer.contains(event.target) && !this.openers.includes(event.target)) this.close(false);
      });
      on(this.layer, 'keydown', event => { if (event.key === 'Escape' && !this.inputs.includes(event.target)) { event.preventDefault(); this.close(true); } });
      on(window, 'resize', () => this.resize());
      on(window, 'scroll', () => this.position());
      if (window.visualViewport) {
        on(window.visualViewport, 'resize', () => this.position());
        on(window.visualViewport, 'scroll', () => this.position());
      }
      this.resizeObserver = new ResizeObserver(() => this.position());
      this.resizeObserver.observe(this.header);
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.requestTimeout);
      this.layer?.remove();
      this.layer = null;
    }

    isMobile() { return this.desktopInput.getBoundingClientRect().width === 0; }

    open(input) {
      this.currentInput = input;
      // Desktop already has its editable field: show results only after typing.
      if (!this.isMobile() && !input.value.trim()) { this.close(false); return; }
      this.layer.hidden = false;
      this.inputs.forEach(item => item.setAttribute('aria-expanded', String(item === input)));
      this.openers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.position();
    }

    close(restoreFocus) {
      if (this.layer.hidden) return;
      this.layer.hidden = true;
      this.inputs.forEach(input => input.setAttribute('aria-expanded', 'false'));
      this.openers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.select(-1);
      if (restoreFocus && this.isMobile()) this.opener?.focus({ preventScroll: true });
      else if (restoreFocus && document.activeElement !== this.desktopInput) {
        this.desktopInput.focus({ preventScroll: true });
        // Focusing the desktop input opens the panel; Escape must leave it closed.
        this.close(false);
      }
    }

    resize() {
      if (this.layer.hidden) return;
      const input = this.isMobile() ? this.mobileInput : this.desktopInput;
      if (this.currentInput !== input) { this.close(false); return; }
      this.position();
    }

    position() {
      if (this.layer.hidden) return;
      const header = this.header.getBoundingClientRect();
      const field = this.desktopInput.form.getBoundingClientRect();
      const viewport = window.visualViewport;
      const visibleTop = viewport?.offsetTop || 0;
      const bottom = visibleTop + (viewport?.height || window.innerHeight);
      const mobile = this.isMobile();
      this.layer.classList.toggle('is-compact', mobile);
      const anchor = mobile ? header.bottom : field.bottom;
      if (anchor < visibleTop || anchor > bottom - 80) { this.close(false); return; }
      const width = Math.min(580, window.innerWidth - (mobile ? 24 : 32));
      const top = Math.max(visibleTop + 8, anchor + 8);
      const edge = mobile ? (this.opener?.getBoundingClientRect().right || window.innerWidth - 12) : field.right;
      const left = Math.max(mobile ? 12 : 16, Math.min(edge - width, window.innerWidth - width - (mobile ? 12 : 16)));
      this.layer.style.setProperty('--ak-search-top', `${top}px`);
      this.layer.style.setProperty('--ak-search-left', `${left}px`);
      this.layer.style.setProperty('--ak-search-width', `${width}px`);
      this.layer.style.setProperty('--ak-search-max-height', `${Math.max(80, bottom - top - 12)}px`);
      this.layer.style.setProperty('--ak-search-overlay-top', `${Math.max(0, header.bottom)}px`);
    }

    update(value) {
      this.inputs.forEach(input => { input.value = value; input.form.querySelector('[type="reset"]').hidden = !value; });
      this.request?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.requestTimeout);
      const sequence = ++this.sequence;
      const query = value.trim();
      this.select(-1);
      this.results.replaceChildren();
      this.options = [];
      this.results.hidden = true;
      this.error.hidden = true;
      this.status.textContent = '';
      this.initial.hidden = Boolean(query);
      this.loading.hidden = !query;
      this.results.setAttribute('aria-busy', String(Boolean(query)));
      this.open(this.currentInput || this.desktopInput);
      if (!query) return;
      this.timer = setTimeout(() => this.search(query, sequence), 220);
    }

    async search(query, sequence) {
      const request = new AbortController();
      this.request = request;
      this.requestTimeout = setTimeout(() => request.abort(), 8000);
      const url = new URL(this.dataset.suggestUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'ak-predictive-search');
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = document.querySelector('[data-ak-search-response]');
        if (!content) throw new Error('Search response missing');
        if (sequence !== this.sequence || !this.isConnected) return;
        this.results.replaceChildren(content);
        this.options = Array.from(this.results.querySelectorAll('[data-ak-search-option]'));
        this.options.forEach((option, index) => { option.id = `ak-search-${this.dataset.searchId}-${index}`; });
        this.results.hidden = false;
        this.status.textContent = content.dataset.announcement;
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.error.hidden = false;
        const fallback = new URL(this.desktopInput.form.action);
        fallback.searchParams.set('q', query);
        fallback.searchParams.set('options[prefix]', 'last');
        this.error.querySelector('a').href = fallback.toString();
      } finally {
        if (sequence === this.sequence && this.isConnected) {
          clearTimeout(this.requestTimeout);
          this.loading.hidden = true;
          this.results.setAttribute('aria-busy', 'false');
          this.position();
        }
      }
    }

    select(index) {
      this.activeIndex = index;
      this.options.forEach((option, itemIndex) => option.setAttribute('aria-selected', String(index === itemIndex)));
      this.inputs.forEach(input => input.removeAttribute('aria-activedescendant'));
      const option = this.options[index];
      if (option && this.currentInput) {
        this.currentInput.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    }

    keydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open(event.target);
        if (this.options.length) {
          const next = this.activeIndex < 0
            ? (event.key === 'ArrowDown' ? 0 : this.options.length - 1)
            : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
          this.select(next);
        }
      }
      if (event.key === 'Enter' && !this.layer.hidden && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }
  }

  customElements.define('ak-inline-search', InlineSearch);
})();
